import { PromptData } from '@/types/agent';
import {
  LearningPath,
  LearningPathAgent,
  UserLearningPathResponse,
} from '@/types/learning-path';

export function getSoulMapLearningPathTag(): string {
  return import.meta.env.VITE_SOULMAP_LEARNING_PATH_TAG?.trim() || 'Counselor';
}

export function sortLearningPathPrompts(
  prompts: LearningPathAgent[] | undefined
): LearningPathAgent[] {
  return [...(prompts ?? [])].sort((a, b) => a.order - b.order);
}

/** First learning path returned for the Soul Map tag (e.g. Counselor). */
export function getSoulMapLearningPath(
  response: UserLearningPathResponse | undefined
): LearningPath | undefined {
  return response?.results?.[0];
}

/** Prompt at the given step index (0 = session 1), ordered by learning-path `order`. */
export function getSoulMapStepPrompt(
  learningPath: LearningPath | undefined,
  stepIndex = 0
): PromptData | undefined {
  const ordered = sortLearningPathPrompts(learningPath?.prompts);
  return ordered[stepIndex]?.prompt;
}

export function resolveSoulMapStepPrompt(
  response: UserLearningPathResponse | undefined,
  stepIndex = 0
): PromptData | undefined {
  return getSoulMapStepPrompt(getSoulMapLearningPath(response), stepIndex);
}

/** True when the practice agent belongs to the Soul Map learning path. */
export function isSoulMapPracticeAgent(
  agentName: string | undefined | null,
  learningPath: LearningPath | undefined
): boolean {
  const normalized = agentName?.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  const learningPathNames = sortLearningPathPrompts(learningPath?.prompts)
    .map((entry) => entry.prompt?.name?.trim().toLowerCase())
    .filter(Boolean);

  return learningPathNames.includes(normalized);
}

/** Soul Map agents include "Soul Map" in the prompt / action name. */
export function isSoulMapNamedAgent(agentName: string | undefined | null): boolean {
  const normalized = agentName?.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return normalized.includes('soul map');
}

/**
 * Detect Soul Map chat by agent name (enterprise Action LP, consumer, etc.).
 * The chat's own action title wins: the selected prompt lives in a shared
 * store and can still belong to a previously opened chat.
 */
export function shouldUseSoulMapLongSession(options: {
  actionTitle?: string | null;
  promptName?: string | null;
}): boolean {
  if (options.actionTitle?.trim()) {
    return isSoulMapNamedAgent(options.actionTitle);
  }

  return isSoulMapNamedAgent(options.promptName);
}
